import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { InterventoRecap } from "@/components/InterventoRecap";
import { CalendarDays, ArrowRight, Loader2, FileText, Download, MessageCircle, Bell, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const REPORT_PDF = "/documenti/report-riunione-19-giugno-2026.pdf";
const WHATSAPP_BACHECA_LINK = "https://chat.whatsapp.com/IXqhxMIrOWP6xYiReWjClZ";

export default function EventiPage() {
  const [events, setEvents] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetch(`${API}/api/events`)
      .then((r) => r.json())
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <Layout>
      <section className="bg-[#1f2d22] text-white py-16 sm:py-20" data-testid="eventi-page-header">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-5">
            <CalendarDays className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Eventi e incontri
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Calendario <span className="italic font-light text-[#E8C9A0]">eventi.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            Tutti gli appuntamenti pubblici dell'associazione Per Asciano.
          </p>
        </div>
      </section>

      {/* Resoconto intervento 11 Luglio — foto, video e mappa */}
      <InterventoRecap showCta={false} />

      <section className="py-16 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {events === null ? (
            <div className="flex items-center justify-center py-16 text-[#5C5C5C]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Caricamento…
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessun evento in programma.</div>
          ) : (
            <div className="space-y-4">
              {events.map((e) => {
                const isRiunione = e.slug === "prima-riunione-aperta-ai-cittadini";
                return (
                <article
                  key={e.id}
                  data-testid={`event-card-${e.slug}`}
                  className="grid sm:grid-cols-12 gap-5 sm:gap-8 items-center bg-white border-2 border-[#E5E1D8] rounded-2xl p-5 sm:p-7 hover:border-[#2E4F3B] transition-all"
                >
                  <div className="sm:col-span-3 sm:border-r sm:border-[#E5E1D8] sm:pr-6">
                    <div className={`text-[10px] font-bold uppercase tracking-[0.22em] ${e.is_past ? "text-[#2E4F3B]" : "text-[#C15C3D]"}`}>
                      {e.is_past ? "Concluso" : "In programma"}
                    </div>
                    <div className="font-display text-xl sm:text-2xl font-black text-[#1A1A1A] leading-tight mt-1">
                      {e.date_label}
                    </div>
                    {e.time_label && <div className="text-xs sm:text-sm text-[#5C5C5C] mt-1">{e.time_label}</div>}
                  </div>
                  <div className="sm:col-span-7">
                    <h3 className="font-display text-xl sm:text-2xl font-black text-[#1A1A1A] leading-tight">
                      {e.title}
                    </h3>
                    <div className="text-sm text-[#5C5C5C] mt-1.5 font-semibold">{e.location}</div>
                    <p className="mt-3 text-sm sm:text-base text-[#5C5C5C] leading-relaxed">{e.description}</p>

                    {isRiunione && e.is_past && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <a
                          href={REPORT_PDF}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-report-view-${e.slug}`}
                          className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] hover:bg-[#2E4F3B] text-white text-xs font-bold px-4 py-2 transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Apri il report
                        </a>
                        <a
                          href={REPORT_PDF}
                          download
                          data-testid={`event-report-download-${e.slug}`}
                          className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] text-xs font-bold px-4 py-2 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Scarica il report
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2 flex sm:justify-end">
                    {e.link ? (
                      <a
                        href={e.link}
                        target={e.link.startsWith("http") ? "_blank" : undefined}
                        rel={e.link.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="inline-flex items-center gap-2 text-sm font-bold text-[#2E4F3B] hover:text-[#1f3828]"
                      >
                        Scopri
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-xs text-[#5C5C5C] italic">Dettagli in arrivo</span>
                    )}
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bacheca WhatsApp — resta aggiornato */}
      <section
        data-testid="eventi-whatsapp-bacheca"
        className="relative bg-gradient-to-br from-[#0e6b3a] via-[#128c43] to-[#25D366] text-white py-16 sm:py-20 overflow-hidden"
      >
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-white/[0.08] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-white/[0.05] blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 backdrop-blur px-4 py-1.5 mb-5">
                <Bell className="w-3.5 h-3.5" />
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em]">
                  Resta aggiornato
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight">
                Non perderti <span className="italic font-light">nessun evento.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
                Vuoi essere il primo a sapere quando viene organizzato un nuovo
                evento o una riunione? Iscriviti alla nostra{" "}
                <strong>bacheca WhatsApp</strong>: solo annunci ufficiali
                dell'associazione, niente spam, niente chat di gruppo.
              </p>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-white text-[#1A1A1A] rounded-3xl p-6 sm:p-7 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#25D366] text-white flex items-center justify-center">
                    <MessageCircle className="w-6 h-6" fill="currentColor" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5C5C]">
                      Bacheca WhatsApp
                    </div>
                    <div className="font-display font-black text-lg leading-tight">
                      Per Asciano
                    </div>
                  </div>
                </div>
                <a
                  href={WHATSAPP_BACHECA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="eventi-whatsapp-cta"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb855] text-white font-bold text-sm sm:text-base px-5 py-3.5 transition-colors shadow-md"
                >
                  Iscriviti alla bacheca
                  <ExternalLink className="w-4 h-4" />
                </a>
                <p className="mt-3 text-[11px] text-[#5C5C5C] text-center leading-relaxed">
                  Apre WhatsApp. Solo annunci, nessuna chat di gruppo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
